/**
 * Relay retry discipline for the DigiLocker return leg (retest items 37/38 —
 * "DigiLocker stuck customer" / "glitch = outage").
 *
 * A customer who COMPLETED the government journey and then hit a transient
 * relay failure was terminal-failed for a provider wobble. The be holds the
 * attempt open (the redeem is idempotent), so the honest client behaviour is:
 *
 *   • bounded, capped retry with backoff (3 attempts ~1s/3s/9s) BEFORE the
 *     'failed' phase ever renders — a blip never surfaces as an outage;
 *   • retry ONLY what retrying can fix: network faults and 5xx. A 4xx is the
 *     server ANSWERING (bad state, expired attempt, refused input) — retrying
 *     the same request re-asks a question already answered;
 *   • an EXPIRED verdict (410/404, or the attempt row saying EXPIRED) means
 *     the held attempt is genuinely dead — only then does retry mean begin(),
 *     because re-relaying discards nothing (there is nothing left to keep).
 *
 * Pure + injectable timers so the cap and schedule are pinned by tests.
 */

import { ApiError } from '@/api/client';

/** The capped backoff schedule — 3 client retries after the initial attempt. */
export const RELAY_RETRY_DELAYS_MS: readonly number[] = [1_000, 3_000, 9_000];

/** Retry can fix: network faults (status 0) and server faults (5xx). */
export function isRetryableRelayError(e: unknown): boolean {
  if (!(e instanceof ApiError)) return false; // non-HTTP throw — do not loop on a bug
  return e.status === 0 || e.status >= 500;
}

/** The held attempt is gone server-side — a fresh begin() is the only path. */
export function isExpiredRelayError(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 404 || e.status === 410);
}

/**
 * Run `attempt` with the capped backoff. Resolves with the first success;
 * rejects with the LAST error once the cap is reached or the error is not
 * retryable. `delay` is injectable for tests (defaults to real timers).
 */
export async function relayWithBackoff<T>(
  attempt: () => Promise<T>,
  opts: {
    delays?: readonly number[];
    delay?: (ms: number) => Promise<void>;
  } = {},
): Promise<T> {
  const delays = opts.delays ?? RELAY_RETRY_DELAYS_MS;
  const wait =
    opts.delay ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let lastError: unknown;
  for (let i = 0; i <= delays.length; i += 1) {
    try {
      return await attempt();
    } catch (e) {
      lastError = e;
      const outOfRetries = i === delays.length;
      if (outOfRetries || !isRetryableRelayError(e)) throw e;
      await wait(delays[i]!);
    }
  }
  throw lastError; // unreachable — the loop always returns or throws
}
